import {
  AccountApiFormData,
  AccountFormData,
  BankConnectionsFormData,
  BillingAddressFormData,
  ContactPartner,
  ContractPartner,
} from '@/types';

// Combined form data interface for the Account tabs (matching AccountFormTabs.tsx)
export interface CombinedAccountFormData {
  general?: Partial<AccountFormData>;
  billing?: Partial<BillingAddressFormData>;
  banking?: BankConnectionsFormData;
  contractPartners?: ContractPartner[];
  contactPartners?: ContactPartner[];
}

/**
 * Adapts the local Account form data to API format
 * Transforms the CombinedAccountFormData structure to the format expected by the API
 *
 * @param token - The Account update token
 * @param localFormData - Combined form data from all Account tabs
 * @returns API-formatted Account form data
 */
export function adaptAccountFormDataForApi(
  token: string,
  localFormData: CombinedAccountFormData
): AccountApiFormData {
  const general = localFormData.general;
  const billing = localFormData.billing;
  const banking = localFormData.banking;
  const contractPartners = localFormData.contractPartners || [];
  const contactPartners = localFormData.contactPartners || [];

  // Check if billing address is different (using the toggle from the form)
  const billingAddressDifferent = billing?.isDifferentFromAccount || false;

  return {
    token,

    // General information mapping
    company_type: general?.companyType,
    language: general?.language,
    name: general?.name,
    name_additional: general?.nameAddition,
    country: general?.country,
    street: general?.street,
    street_number: general?.streetNo,
    postal: general?.poBox, // API uses 'postal'
    postcode: general?.postalCode, // API also accepts 'postcode'
    city: general?.city,
    canton: general?.canton,
    website: general?.website,
    industry: general?.industry,
    commercial_register_number: general?.commercialRegisterNumber,

    // Billing address information (using proper BillingAddressFormData field names)
    billing_address_different: billingAddressDifferent,
    billing_name: billing?.billingName,
    billing_country: billing?.billingCountry,
    billing_street: billing?.billingStreet,
    billing_street_number: billing?.billingHouseNumber,
    billing_postal: billing?.billingPostBox,
    billing_postcode: billing?.billingPostalCode,
    billing_city: billing?.billingCity,
    billing_canton: billing?.billingCanton,
    billing_language: billing?.billingLanguage,

    // Email preferences (from BillingAddressFormData)
    email_invoices_reminders: billing?.emailInvoicesReminders,
    email_coverage_money_orders: billing?.emailCoverageMoneyOrders,
    email_reporting: billing?.emailReporting,

    // Bank details mapping (using BankConnectionData from BankConnectionsFormData)
    bank_details: banking?.bankConnections?.map(connection => ({
      currencies: connection.currency,
      beneficiary: connection.beneficiary,
      bank_name: connection.bankName,
      bank_location: connection.bankLocation,
      clearing_number: connection.clearingNumber,
      account_number: connection.accountNumber,
      iban_number: connection.iban,
      swift_code: connection.swiftCode,
      booking_text: connection.bookingText || '',
    })),

    // Contract accounts mapping
    contract_accounts: contractPartners.map(partner => ({
      title: partner.title,
      first_name: partner.firstName,
      last_name: partner.lastName,
      phone: partner.phone || partner.mobile, // Use phone first, fallback to mobile
      email: partner.email,
      language: partner.language,
      role: partner.role,
    })),

    // Contact accounts mapping
    contact_accounts: contactPartners.map(partner => ({
      title: partner.title,
      first_name: partner.firstName,
      last_name: partner.lastName,
      phone: partner.phone || partner.mobile, // Use phone first, fallback to mobile
      email: partner.email,
      language: partner.language,
      role: partner.function || partner.contactType, // Use function first, fallback to contactType
    })),
  };
}

/**
 * Validates that all required fields are present before API submission
 *
 * @param formData - Local form data to validate
 * @param t - Optional translation function for error messages
 * @returns Array of validation error messages
 */
export function validateAccountFormData(
  formData: CombinedAccountFormData,
  t?: (key: string) => string
): string[] {
  const errors: string[] = [];

  // Token is required but will be provided separately
  if (
    !formData.general &&
    !formData.billing &&
    !formData.contractPartners &&
    !formData.contactPartners
  ) {
    errors.push('At least one form section must be completed');
    return errors;
  }

  // Validate general form if present
  if (formData.general) {
    const general = formData.general;

    if (general.companyType && !general.companyType.trim()) {
      errors.push('Company type cannot be empty if provided');
    }

    if (general.name && !general.name.trim()) {
      errors.push('Company name cannot be empty if provided');
    }

    if (general.country && !general.country.trim()) {
      errors.push('Country cannot be empty if provided');
    }

    if (general.street && !general.street.trim()) {
      errors.push('Street cannot be empty if provided');
    }

    if (general.city && !general.city.trim()) {
      errors.push('City cannot be empty if provided');
    }

    if (general.industry && !general.industry.trim()) {
      errors.push('Industry cannot be empty if provided');
    }

    if (
      general.commercialRegisterNumber &&
      !general.commercialRegisterNumber.trim()
    ) {
      errors.push('Commercial register number cannot be empty if provided');
    }
  }

  // Validate contract partners if present
  if (formData.contractPartners) {
    // Validate that at least one contract partner is provided (REQUIRED)
    if (formData.contractPartners.length === 0) {
      const errorMessage = t
        ? t('account.contractPartnerRequired')
        : 'At least one Contract Partner is required';
      errors.push(errorMessage);
    }

    formData.contractPartners.forEach((partner, index) => {
      if (!partner.firstName?.trim()) {
        errors.push(`Contract partner ${index + 1}: First name is required`);
      }
      if (!partner.lastName?.trim()) {
        errors.push(`Contract partner ${index + 1}: Last name is required`);
      }
      if (!partner.email?.trim()) {
        errors.push(`Contract partner ${index + 1}: Email is required`);
      }
      if (partner.email && !/\S+@\S+\.\S+/.test(partner.email)) {
        errors.push(`Contract partner ${index + 1}: Valid email is required`);
      }
    });
  }

  // Validate contact partners if present
  if (formData.contactPartners) {
    formData.contactPartners.forEach((partner, index) => {
      if (!partner.firstName?.trim()) {
        errors.push(`Contact partner ${index + 1}: First name is required`);
      }
      if (!partner.lastName?.trim()) {
        errors.push(`Contact partner ${index + 1}: Last name is required`);
      }
      if (!partner.email?.trim()) {
        errors.push(`Contact partner ${index + 1}: Email is required`);
      }
      if (partner.email && !/\S+@\S+\.\S+/.test(partner.email)) {
        errors.push(`Contact partner ${index + 1}: Valid email is required`);
      }
    });
  }

  return errors;
}
